import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Layers, Lock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { formatDecimal, formatInteger } from '@/lib/language';
import {
  adsErrorCode,
  previewCurrentValue,
  previewNewValue,
  structureErrorText,
  structureWriteBlock,
  useStructureOverview,
  useStructureWrite,
  type StructureCampaignView,
  type StructureLever,
  type StructureOverview,
  type StructurePreviewBody,
  type StructureWriteVariables,
} from '@/hooks/useAdsStructure';

/**
 * Abschnitt "Kampagnenstruktur" auf `/internal/ads-steuerung` (Ads-Ausbau 0.7,
 * Task 6). Eigene Komponente statt weiterer Inline-Block in `AdsSteuerung.tsx`
 * (die Seite steht bei ueber 2.600 Zeilen, und dieser Abschnitt hat einen
 * eigenen Vertrag -- eigene Function, eigener Hook).
 *
 * Spec `docs/superpowers/specs/2026-08-25-ads-struktur-steuerpult-design.md`
 * Abschnitt 11 und die Delta-Spec
 * `docs/superpowers/specs/2026-09-25-ads-struktur-steuerpult-delta.md`
 * (beide Root-Repo). Wo sie sich widersprechen, gilt die Delta-Spec.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WAS HIER NICHT PASSIERT (harte Grenze, Δ2/Δ11/Spec §15)
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * Kein Knopf hier aktiviert oder pausiert-wieder-ein eine Kampagne. Die
 * Function selbst hat dafuer keinen Op-Builder (Kommentarkopf
 * `ads-structure-run/handlers.ts`): "sie schreibt nie einen
 * Aktivierungs-Status ... das Wieder-Einschalten bleibt eine menschliche
 * Handlung in der Google-Ads-UI." Diese Komponente bildet drei Hebel ab:
 * Tagesbudget, Wertziel, Pausieren -- und Pausieren ist eine Einbahnstrasse.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * DAS VORSCHAU-MUSTER (Spec §10, Bedienmuster 1 bis 6)
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * Jeder Schreibzugriff geht ueber zwei Schritte in DEMSELBEN Dialog:
 *   1. "Vorschau" ruft dieselbe Aktion mit `dry_run: true` -- Google prueft
 *      (`validateOnly`), es wird NICHTS geschrieben. Das Ergebnis lebt im
 *      React-State dieser Komponente, NICHT im Query-Cache (Spec §10.2, harte
 *      Grenze wie `shownRun` beim Tier-Uebernehmen: nach einem Reload ist die
 *      Vorschau weg und muss neu angesehen werden).
 *   2. "Jetzt aendern" sendet denselben Aufruf mit `dry_run: false` und dem
 *      TOCTOU-Pin
 *      (`expected_budget_micros`/`expected_target_roas`/`expected_status`) --
 *      dem Wert, den die Vorschau gesehen hat (`buildConfirmVariables` liest
 *      diesen Wert ueber `previewCurrentValue`/`previewNewValue`, NIE erneut
 *      aus dem Eingabefeld -- ein zweites Parsen desselben Textes koennte in
 *      einer Rundungsecke vom bereits gesehenen Vorschau-Ergebnis abweichen).
 *      Weicht der frisch gelesene Ist-Wert davon ab, lehnt die Function mit
 *      `409 STALE_PREVIEW` ab, und diese Komponente verwirft die Vorschau
 *      (Spec §10.4): erneut ansehen statt blind erneut zu senden. Der Hook
 *      invalidiert bei `STALE_PREVIEW`/`VERIFY_FAILED` selbst die Uebersicht
 *      (I2, Fix-Welle) -- die "Aktuell ..."-Zeile liest danach automatisch den
 *      frischen Stand, weil sie aus der LIVE-Uebersicht abgeleitet wird
 *      (`activeCampaign`), nicht aus einer beim Oeffnen eingefrorenen Kopie.
 *
 * Das Eingabefeld fuer den neuen Wert wird gesperrt, sobald eine Vorschau
 * steht -- die Zahlen im Bestaetigungsdialog muessen exakt die sein, die der
 * reale Lauf sendet (Spec §10.3: "AlertDialog mit exakten Zahlen").
 *
 * "Vorschau" und "Jetzt aendern" sind bewusst schlichte `Button`, keine
 * `AlertDialogAction`: radix' `AlertDialogAction` ist ein `Dialog.Close` und
 * schliesst den Dialog beim Klick, egal was der `onClick`-Handler tut oder wie
 * er ausgeht. Ein "Vorschau"-Klick darf den Dialog nie schliessen, und ein
 * gescheiterter realer Lauf soll die Fehlermeldung an Ort und Stelle zeigen
 * statt den Dialog wegzuraeumen. Der Rahmen bleibt trotzdem ein `AlertDialog`
 * (Ueberlagerung, Fokusfalle, Titel/Beschreibung) -- die Haus-Regel "AlertDialog
 * bei destruktiven Aktionen" gilt fuer den RAHMEN der Interaktion, nicht fuer
 * jeden einzelnen Knopf darin.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WARUM `dialogState` NUR EINEN RESOURCE-NAMEN TRAEGT, KEINE KAMPAGNE
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * Eine fruehere Fassung fror die Kampagne beim Oeffnen des Dialogs ein
 * (`dialogState.campaign: StructureCampaignView`). Das war der Fehler hinter
 * I2: nach einem STALE_PREVIEW oder VERIFY_FAILED invalidiert der Hook die
 * Uebersicht, aber die eingefrorene Kopie im Dialog-State bekam davon nichts
 * mit -- die "Aktuell ..."-Zeile zeigte weiter den Wert von VOR dem
 * gescheiterten Lauf. `activeCampaign` liest stattdessen bei JEDEM Render aus
 * der LIVE-Uebersicht nach (`overview.campaigns.find(...)`), damit ein
 * Refetch automatisch ankommt.
 */

// ═════════════════════════════════════════════════════════════════════════════
// Formatierung (lokale Kopien, Muster `AdsSteuerung.tsx` -- diese Seite hat
// bewusst keinen i18n-Namespace, ihre Copy steht im Quelltext)
// ═════════════════════════════════════════════════════════════════════════════

function int(value: number | null | undefined): string {
  return formatInteger(value, 'de');
}

function dec(value: number | null | undefined, digits = 2): string {
  return formatDecimal(value, 'de', digits);
}

const NO_VALUE = '—';

function microsToUnits(micros: string | null | undefined): number | null {
  if (micros === null || micros === undefined || micros.trim() === '') return null;
  const value = Number(micros);
  return Number.isFinite(value) ? value / 1e6 : null;
}

function formatMoney(micros: string | null | undefined, currency: string | null): string {
  const value = microsToUnits(micros);
  if (value === null) return NO_VALUE;
  return `${dec(value, 2)}${currency ? ` ${currency}` : ''}`;
}

/**
 * Ein Waehrungsbetrag, der NICHT in Micros steht (z.B. `conversions_value`
 * aus der Ads-API, `metrics.conversions_value` als `double` in Kontowaehrung
 * -- anders als `cost_micros`). M8: kein haengendes Leerzeichen, wenn
 * `currency` unbekannt ist -- `${dec(value)} ${currency}` liesse dann ein
 * Leerzeichen nach der Zahl stehen.
 */
function formatCurrencyValue(value: number | null | undefined, currency: string | null): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return NO_VALUE;
  return `${dec(value, 2)}${currency ? ` ${currency}` : ''}`;
}

function formatRatio(value: number | null | undefined): string {
  if (value === null || value === undefined) return NO_VALUE;
  return dec(value, 2);
}

function formatPct(value: number | null | undefined): string {
  if (value === null || value === undefined) return NO_VALUE;
  const sign = value > 0 ? '+' : '';
  return `${sign}${dec(value, 1)} %`;
}

function dateTime(iso: string | null | undefined): string {
  if (!iso) return NO_VALUE;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return NO_VALUE;
  return parsed.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
}

const LEVER_LABEL: Record<StructureLever, string> = {
  budget: 'Budget',
  troas: 'Wertziel',
  pause: 'Pausieren',
};

const ROLE_LABEL: Record<string, string> = {
  hero: 'Hero',
  katalog: 'Katalog',
  zombie: 'Zombie',
};

/**
 * M4 (Task-Review): `GUARDRAIL_BREACH_TODAY` deckt serverseitig ZWEI
 * Zustaende ab -- `input.guardrailToday !== 'clear'`
 * (`_shared/ads-structure-eligibility.ts`), also sowohl einen echten
 * `breach` als auch ein `unknown` (Guardrail-Read gescheitert). Der
 * uebersetzte Text von `STRUCTURE_ERROR_TEXT` behauptet einen Verstoss --
 * richtig bei `breach`, aber irrefuehrend bei `unknown`: dort ist nichts
 * gerissen, es konnte nur nicht gelesen werden. Reine Funktion, damit sie
 * ohne Komponenten-Rendern testbar ist.
 */
export function accountBlockText(overview: StructureOverview): string | null {
  if (!overview.write_blocked_reason) return null;
  if (overview.write_blocked_reason === 'GUARDRAIL_BREACH_TODAY' && overview.guardrail_today === 'unknown') {
    return 'Guardrail-Befund heute nicht lesbar, Schreiben gesperrt.';
  }
  return structureErrorText(overview.write_blocked_reason);
}

// ═════════════════════════════════════════════════════════════════════════════
// Wert-Parsing der Eingabefelder
// ═════════════════════════════════════════════════════════════════════════════

/** CHF/Tag-Eingabe (deutsches Komma erlaubt) -> Micros als String, oder `null`. */
function parseChfToMicros(text: string): string | null {
  const normalized = text.trim().replace(',', '.');
  if (normalized === '' || !/^\d+(\.\d+)?$/.test(normalized)) return null;
  const value = Number(normalized);
  if (!Number.isFinite(value) || value <= 0) return null;
  const micros = Math.round(value * 1e6);
  return micros > 0 ? String(micros) : null;
}

/**
 * Wertziel-Eingabe (Verhaeltnis, z.B. 5,50) -> Zahl im serverseitig erlaubten
 * Band.
 *
 * ⚠ SYNC REQUIRED mit `LEVERS.set_troas.parseNew`
 * (`supabase/functions/ads-structure-run/handlers.ts:627-631`): das Band
 * 0,5 bis 50 steht dort als harte Server-Grenze. Eine Aenderung dort ohne
 * diese Zeile liesse die Vorschau hier Werte zulassen, die der reale Lauf mit
 * `409 VALUE_REQUIRED` ablehnt -- kein Test faengt das automatisch, weil die
 * Function unter Deno laeuft und ihre Konstante von hier aus nicht
 * importierbar ist (Muster `adsScopeIsolation.test.ts`).
 */
function parseTroasRatio(text: string): number | null {
  const normalized = text.trim().replace(',', '.');
  if (normalized === '' || !/^\d+(\.\d+)?$/.test(normalized)) return null;
  const value = Number(normalized);
  if (!Number.isFinite(value) || value < 0.5 || value > 50) return null;
  return value;
}

// ═════════════════════════════════════════════════════════════════════════════
// Kleinteile
// ═════════════════════════════════════════════════════════════════════════════

function BlockNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
      <Lock className="mt-0.5 h-3 w-3 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

function WarnBanner({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-warning/40 bg-warning-light p-4">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning-ink" />
      <div className="space-y-1">
        <p className="text-sm font-semibold text-warning-ink">{title}</p>
        {children && <div className="text-sm text-warning-ink">{children}</div>}
      </div>
    </div>
  );
}

/** Nur die Identitaet, nicht die Kampagne selbst -- siehe Kommentarkopf. */
interface DialogState {
  campaignResourceName: string;
  lever: StructureLever;
}

interface LeverButtonProps {
  lever: StructureLever;
  campaign: StructureCampaignView;
  overview: StructureOverview;
  onOpen: (campaign: StructureCampaignView, lever: StructureLever) => void;
}

/** Ein Hebel-Knopf einer Kampagnenzeile: entweder aktiv, oder gesperrt mit Grund (Spec §10.6). */
function LeverButton({ lever, campaign, overview, onOpen }: LeverButtonProps) {
  const block = structureWriteBlock(overview, campaign, lever);
  const label = lever === 'troas'
    ? (campaign.bidding.target_roas === null ? 'Wertziel setzen' : 'Wertziel ändern')
    : lever === 'budget' ? 'Budget ändern' : 'Pausieren';

  if (block) {
    return (
      <div className="space-y-1">
        <Button size="sm" variant="outline" disabled>
          {label}
        </Button>
        <BlockNote>{structureErrorText(block)}</BlockNote>
      </div>
    );
  }

  return (
    <Button size="sm" variant="outline" onClick={() => onOpen(campaign, lever)}>
      {label}
    </Button>
  );
}

function RoleBadge({ role, isStructure }: { role: string | null; isStructure: boolean }) {
  if (!isStructure) return <Badge variant="outline">Alt-Kampagne</Badge>;
  const label = role ? ROLE_LABEL[role] ?? role : null;
  return label ? <Badge variant="secondary">{label}</Badge> : null;
}

function MarkersCell({ campaign }: { campaign: StructureCampaignView }) {
  const markers: React.ReactNode[] = [];
  if (campaign.evaluation_window) {
    markers.push(
      <Badge key="eval" variant="warning" className="whitespace-nowrap">
        Bewertungsfenster läuft, nicht bewerten
      </Badge>,
    );
  }
  if (campaign.drift.budget) {
    markers.push(
      <Badge key="drift-budget" variant="outline" className="whitespace-nowrap">
        Budget von Hand geändert?
      </Badge>,
    );
  }
  if (campaign.drift.troas) {
    markers.push(
      <Badge key="drift-troas" variant="outline" className="whitespace-nowrap">
        Wertziel von Hand geändert?
      </Badge>,
    );
  }
  if (campaign.bidding.system_status) {
    markers.push(
      <Badge key="bidding-status" variant="outline" className="whitespace-nowrap">
        {campaign.bidding.system_status}
      </Badge>,
    );
  }
  if (markers.length === 0) return <span className="text-xs text-muted-foreground">{NO_VALUE}</span>;
  return <div className="flex flex-wrap gap-1">{markers}</div>;
}

/**
 * Die Vorher/Nachher-Zeile der Vorschau, ueber alle drei Hebel hinweg mit
 * `previewCurrentValue`/`previewNewValue` gelesen statt per Feldzugriff
 * (M6, Task-Review: die beiden Helfer aus dem Hook hatten sonst keinen
 * Aufrufer -- toter Exportcode).
 */
function PreviewSummaryLine({ preview, currency }: { preview: StructurePreviewBody; currency: string | null }) {
  const currentRaw = previewCurrentValue(preview);
  const newRaw = previewNewValue(preview);

  if (preview.lever === 'budget') {
    return (
      <>
        {formatMoney(typeof currentRaw === 'string' ? currentRaw : null, currency)} →{' '}
        {formatMoney(typeof newRaw === 'string' ? newRaw : null, currency)}/Tag ({formatPct(preview.change_pct)})
      </>
    );
  }
  if (preview.lever === 'troas') {
    const current = typeof currentRaw === 'number' ? currentRaw : null;
    const next = typeof newRaw === 'number' ? newRaw : null;
    return (
      <>
        {preview.first_set
          ? `erstmals ${formatRatio(next)} (Anker Ist-ROAS ${formatRatio(preview.anchor_roas)})`
          : `${formatRatio(current)} → ${formatRatio(next)}`}{' '}
        ({formatPct(preview.change_pct)})
      </>
    );
  }
  return <>{currentRaw} → {newRaw}</>;
}

// ═════════════════════════════════════════════════════════════════════════════
// Hauptkomponente
// ═════════════════════════════════════════════════════════════════════════════

export interface AdsStructureSectionProps {
  customerId: string;
  accountLabel: string;
}

export function AdsStructureSection({ customerId, accountLabel }: AdsStructureSectionProps) {
  const overviewQuery = useStructureOverview(customerId);
  const writeMutation = useStructureWrite();

  const [dialogState, setDialogState] = useState<DialogState | null>(null);
  const [valueInput, setValueInput] = useState('');
  const [reasonInput, setReasonInput] = useState('');
  const [preview, setPreview] = useState<StructurePreviewBody | null>(null);
  const [previewErrorCode, setPreviewErrorCode] = useState<string | null>(null);
  const [previewPending, setPreviewPending] = useState(false);
  const [confirmPending, setConfirmPending] = useState(false);
  const valueInputRef = useRef<HTMLInputElement>(null);

  // M3 (Task-Review): jede oeffnende/schliessende Handlung erhoeht die
  // Sitzungs-Kennung. `handlePreview` merkt sich den Stand VOR dem Warten auf
  // die Antwort und verwirft eine sptaer eintreffende Antwort, wenn sich der
  // Stand inzwischen geaendert hat -- der Dialog wurde geschlossen, oder ein
  // ANDERER Knopf (andere Kampagne oder anderer Hebel) wurde geoeffnet,
  // waehrend die alte Vorschau noch unterwegs war. Ohne diesen Riegel koennte
  // eine spaet eintreffende Antwort der vorigen Anfrage die Vorschau der
  // NEUEN Sitzung ueberschreiben.
  const dialogSessionRef = useRef(0);

  const overview = overviewQuery.data ?? null;
  const currency = overview?.account.currency ?? null;

  // Live nachgeschlagen statt beim Oeffnen eingefroren -- siehe Kommentarkopf
  // "WARUM dialogState NUR EINEN RESOURCE-NAMEN TRAEGT".
  const activeCampaign = dialogState
    ? overview?.campaigns.find((c) => c.resource_name === dialogState.campaignResourceName) ?? null
    : null;

  function closeDialog() {
    dialogSessionRef.current += 1;
    setDialogState(null);
    setPreview(null);
    setPreviewErrorCode(null);
    setValueInput('');
    setReasonInput('');
    setPreviewPending(false);
    setConfirmPending(false);
  }

  function openDialog(campaign: StructureCampaignView, lever: StructureLever) {
    dialogSessionRef.current += 1;
    setDialogState({ campaignResourceName: campaign.resource_name, lever });
    setPreview(null);
    setPreviewErrorCode(null);
    setReasonInput('');
    if (lever === 'budget') {
      const units = microsToUnits(campaign.budget.amount_micros);
      setValueInput(units === null ? '' : units.toFixed(2));
    } else if (lever === 'troas') {
      setValueInput(campaign.bidding.target_roas === null ? '' : dec(campaign.bidding.target_roas, 2));
    } else {
      setValueInput('');
    }
  }

  function buildPreviewVariables(): StructureWriteVariables | null {
    if (!dialogState) return null;
    const { lever } = dialogState;
    const base = {
      ads_customer_id: customerId,
      campaign_resource_name: dialogState.campaignResourceName,
      dry_run: true as const,
    };
    if (lever === 'budget') {
      const micros = parseChfToMicros(valueInput);
      if (micros === null) return null;
      return { action: 'set_budget', new_budget_micros: micros, ...base };
    }
    if (lever === 'troas') {
      const ratio = parseTroasRatio(valueInput);
      if (ratio === null) return null;
      return { action: 'set_troas', new_target_roas: ratio, ...base };
    }
    return { action: 'campaign_pause', ...base };
  }

  async function handlePreview() {
    const session = dialogSessionRef.current;
    const vars = buildPreviewVariables();
    if (!vars) {
      setPreviewErrorCode('VALUE_REQUIRED');
      return;
    }
    setPreviewPending(true);
    setPreviewErrorCode(null);
    try {
      const result = await writeMutation.mutateAsync(vars);
      if (dialogSessionRef.current !== session) return; // M3: veraltete Antwort, Dialog ist weiter
      if (result.dry_run === true) setPreview(result.preview);
    } catch (error) {
      if (dialogSessionRef.current !== session) return;
      setPreview(null);
      setPreviewErrorCode(adsErrorCode(error) ?? 'DATABASE_ERROR');
    } finally {
      if (dialogSessionRef.current === session) setPreviewPending(false);
    }
  }

  function buildConfirmVariables(): StructureWriteVariables | null {
    if (!dialogState || !preview || !activeCampaign) return null;
    // M3: die Vorschau muss zur GERADE geoeffneten Kampagne und zum GERADE
    // gewaehlten Hebel gehoeren -- eine Absicherung zusaetzlich zur
    // Sitzungskennung oben, falls der State je auf anderem Weg auseinanderlaeuft.
    if (preview.campaign !== activeCampaign.resource_name || preview.lever !== dialogState.lever) return null;

    const { lever } = dialogState;
    const reason = reasonInput.trim();
    const base = {
      ads_customer_id: customerId,
      campaign_resource_name: activeCampaign.resource_name,
      dry_run: false as const,
      ...(reason ? { reason } : {}),
    };

    // Beide Werte kommen aus der VORSCHAU, nie aus einem erneuten Parsen des
    // Eingabefelds (Kommentarkopf, Abschnitt "DAS VORSCHAU-MUSTER").
    const expectedRaw = previewCurrentValue(preview);
    const newRaw = previewNewValue(preview);

    if (lever === 'budget') {
      if (typeof newRaw !== 'string' || typeof expectedRaw !== 'string') return null;
      return { action: 'set_budget', new_budget_micros: newRaw, expected_budget_micros: expectedRaw, ...base };
    }
    if (lever === 'troas') {
      if (typeof newRaw !== 'number') return null;
      // `null` ist ein gueltiger Pin: "es war noch kein Ziel gesetzt"
      // (Erstsetzung, Server-Kommentar `hasExpected`).
      const expectedTarget = typeof expectedRaw === 'number' ? expectedRaw : null;
      return { action: 'set_troas', new_target_roas: newRaw, expected_target_roas: expectedTarget, ...base };
    }
    if (typeof newRaw !== 'string' || typeof expectedRaw !== 'string' || !expectedRaw) return null;
    return { action: 'campaign_pause', expected_status: expectedRaw, ...base };
  }

  async function handleConfirm() {
    // M9 (Re-Review): dieselbe Sitzungs-Absicherung wie `handlePreview`
    // (M3) -- bis hierher fehlte sie hier. Ohne sie konnte ein
    // Abbrechen/Neuoeffnen WAEHREND der reale Lauf noch unterwegs war die
    // Vorschau-/Fehler-Felder der inzwischen geoeffneten NEUEN Sitzung
    // ueberschreiben, sobald die alte Antwort eintraf. Toasts und die
    // Invalidierung im Hook (`useStructureWrite`) bleiben UNBEDINGT --
    // beides sind Aussagen ueber den abgeschlossenen Google-Schreibzugriff
    // selbst, unabhaengig davon, welcher Dialog gerade offen ist.
    const session = dialogSessionRef.current;
    const vars = buildConfirmVariables();
    if (!vars) {
      toast.error('Wert unvollständig', { description: 'Bitte die Vorschau erneut ansehen.' });
      return;
    }
    setConfirmPending(true);
    try {
      const result = await writeMutation.mutateAsync(vars);
      if (result.dry_run === false) {
        const leverLabel = LEVER_LABEL[
          vars.action === 'set_budget' ? 'budget' : vars.action === 'set_troas' ? 'troas' : 'pause'
        ];
        const warningsLine = result.warnings.length > 0
          ? ` Hinweise: ${result.warnings.join(', ')}.`
          : '';

        if (!result.logged) {
          toast.warning('Geändert, aber NICHT protokolliert', {
            description:
              'Der Schreibzugriff ist bei Google angekommen, der Eintrag ins Änderungs-Log ist '
              + `gescheitert. Bitte von Hand nachtragen. ${result.log_error ?? ''}`,
            duration: 20000,
          });
        } else {
          toast.success('Geändert und protokolliert', {
            description: `${accountLabel}: ${leverLabel} geändert (${formatPct(result.change_pct)}).${warningsLine}`,
          });
        }

        // I1 (Task-Review): zwei weitere, UNABHAENGIGE Signale desselben
        // erfolgreichen Laufs -- beide koennen zusaetzlich zum obigen
        // Erfolgs-/Protokoll-Toast auftreten, keines schliesst das andere aus.
        if (result.mutate_warning) {
          toast.warning('Google meldete beim Schreiben einen Fehler', {
            description:
              'Das anschließende Nachlesen bestätigt trotzdem den neuen Wert -- der Schreibzugriff '
              + `ist also angekommen. Google-Meldung: ${result.mutate_warning}`,
            duration: 20000,
          });
        }
        if (result.lever === 'budget' && result.budget_sum && !result.budget_sum.shifted) {
          toast.warning('Strukturbudget nicht nachgezogen', {
            description:
              'Die Kampagne trägt den neuen Wert, die Summe in ads_rollout_accounts aber nicht. '
              + 'Bitte von Hand korrigieren, sonst meldet Guardrail G-C morgen einen Verstoß. '
              + `${result.budget_sum.error ?? ''}`,
            duration: 20000,
          });
        }

        // M9: nur schliessen, wenn dieser Lauf noch zur AKTUELLEN Sitzung
        // gehoert -- sonst raeumt er einen Dialog weg, den der Nutzer
        // inzwischen neu geoeffnet hat.
        if (dialogSessionRef.current === session) closeDialog();
      }
    } catch (error) {
      const code = adsErrorCode(error);
      if (code === 'STALE_PREVIEW') {
        // Spec §10.4: die Vorschau wird verworfen, nicht stillschweigend
        // erneut gesendet. Der Hook invalidiert die Uebersicht selbst (I2);
        // die "Aktuell ..."-Zeile liest danach den frischen Stand ueber
        // `activeCampaign`. M9: nur an der AKTUELLEN Sitzung, sonst
        // ueberschriebe eine veraltete Antwort die frisch geoeffnete.
        if (dialogSessionRef.current === session) {
          setPreview(null);
          setPreviewErrorCode('STALE_PREVIEW');
        }
      } else {
        toast.error('Änderung abgelehnt', { description: structureErrorText(code ?? 'DATABASE_ERROR') });
      }
    } finally {
      if (dialogSessionRef.current === session) setConfirmPending(false);
    }
  }

  const measurementWindowActive = overview?.measurement_window_active ?? false;
  // M2 (Task-Review): die Vorschau kennt `reason_required` bereits (Server-
  // Urteil zum Vorschau-Zeitpunkt); ohne Vorschau faellt die Frage auf das
  // Konto-Feld zurueck. ODER-Verknuepfung mit Absicht -- die sichere Richtung
  // ist "eher einmal zu viel nach einer Begruendung fragen".
  const reasonRequired = (preview?.reason_required ?? false) || measurementWindowActive;
  const reasonMissing = reasonRequired && reasonInput.trim() === '';
  const confirmDisabled = confirmPending || !preview || reasonMissing;

  return (
    <section className="space-y-3" aria-labelledby="ads-structure-heading">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="ads-structure-heading" className="text-lg font-semibold">
          Kampagnenstruktur
        </h2>
      </div>

      {measurementWindowActive && (
        <WarnBanner title="Messfenster 22.09. bis 19.10.">
          Jeder Eingriff braucht eine Begründung und wird in der Auswertung als Einschränkung
          ausgewiesen (Messplan-Nachtrag E1, Kontroll-Vorbehalt Bedingung 1).
        </WarnBanner>
      )}

      {overviewQuery.isLoading ? (
        <Card>
          <CardContent className="space-y-2 py-6">
            <Skeleton className="h-6 w-1/3" />
            <Skeleton className="h-24 w-full" />
          </CardContent>
        </Card>
      ) : overviewQuery.isError ? (
        <ErrorState
          title="Kampagnenstruktur nicht geladen"
          description={structureErrorText(adsErrorCode(overviewQuery.error) ?? 'DATABASE_ERROR')}
          onRetry={() => void overviewQuery.refetch()}
        />
      ) : !overview ? null : (
        <>
          <Card>
            <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-1 py-4 text-sm">
              <span>
                <span className="text-muted-foreground">Strukturbudget: </span>
                {formatMoney(overview.account.structure_budget_micros, currency)}/Tag
              </span>
              <span>
                <span className="text-muted-foreground">Guardrails heute: </span>
                {overview.guardrail_today === 'breach'
                  ? 'Verstoß'
                  : overview.guardrail_today === 'clear'
                    ? 'sauber'
                    : 'unbekannt'}
              </span>
              {accountBlockText(overview) && (
                <span className="font-medium text-warning-ink">{accountBlockText(overview)}</span>
              )}
            </CardContent>
          </Card>

          {overview.notes.length > 0 && (
            <div className="space-y-1">
              {overview.notes.map((note, index) => (
                <p key={index} className="text-xs text-muted-foreground">
                  {note}
                </p>
              ))}
            </div>
          )}

          {overview.campaigns.length === 0 ? (
            <EmptyState
              icon={Layers}
              title={`Keine Kampagnen für ${accountLabel}`}
              description="Dieses Konto meldet keine einzige Kampagne. Entweder ist es leer, oder das MCC ist nicht erreichbar."
            />
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kampagne</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Typ</TableHead>
                    <TableHead>Budget</TableHead>
                    <TableHead>Bidding</TableHead>
                    <TableHead className="text-right">Spend 7T</TableHead>
                    <TableHead className="text-right">Spend 30T</TableHead>
                    <TableHead className="text-right">Conv. 30T</TableHead>
                    <TableHead className="text-right">Wert 30T</TableHead>
                    <TableHead className="text-right">Ist-ROAS 30T</TableHead>
                    <TableHead>Marken</TableHead>
                    <TableHead>Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {overview.campaigns.map((campaign) => (
                    <TableRow key={campaign.resource_name}>
                      <TableCell className="max-w-[220px]">
                        <div className="space-y-1">
                          <p className="truncate font-medium" title={campaign.name ?? campaign.resource_name}>
                            {campaign.name ?? campaign.resource_name}
                          </p>
                          <RoleBadge role={campaign.role} isStructure={campaign.is_structure} />
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{campaign.status ?? NO_VALUE}</TableCell>
                      <TableCell className="text-sm">{campaign.channel_type ?? NO_VALUE}</TableCell>
                      <TableCell className="text-sm">
                        {formatMoney(campaign.budget.amount_micros, currency)}/Tag
                        {campaign.budget.explicitly_shared && (
                          <span className="block text-xs text-muted-foreground">geteilt</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {campaign.bidding.target_roas !== null ? (
                          <>ROAS {formatRatio(campaign.bidding.target_roas)}</>
                        ) : campaign.bidding.cpc_ceiling_micros !== null ? (
                          <>MaxClicks, Deckel {formatMoney(campaign.bidding.cpc_ceiling_micros, currency)}</>
                        ) : (
                          <>ohne Ziel</>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">
                        {formatMoney(String(campaign.spend_7d.cost_micros), currency)}
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">
                        {formatMoney(String(campaign.spend_30d.cost_micros), currency)}
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">
                        {int(campaign.spend_30d.conversions)}
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">
                        {formatCurrencyValue(campaign.spend_30d.conversions_value, currency)}
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">
                        {formatRatio(campaign.measured_roas_30d)}
                      </TableCell>
                      <TableCell>
                        <MarkersCell campaign={campaign} />
                      </TableCell>
                      <TableCell>
                        {!campaign.is_structure ? (
                          <p className="text-xs text-muted-foreground">Alt-Kampagne, unantastbar</p>
                        ) : (
                          <div className="flex flex-col gap-2">
                            <LeverButton lever="budget" campaign={campaign} overview={overview} onOpen={openDialog} />
                            <LeverButton lever="troas" campaign={campaign} overview={overview} onOpen={openDialog} />
                            <LeverButton lever="pause" campaign={campaign} overview={overview} onOpen={openDialog} />
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}

      <AlertDialog open={dialogState !== null} onOpenChange={(open) => { if (!open) closeDialog(); }}>
        <AlertDialogContent
          onOpenAutoFocus={(event) => {
            // M7: das Eingabefeld bekommt den Fokus, sobald es existiert
            // (Budget/Wertziel). Bei Pausieren gibt es keins -- dort greift
            // radix' Standardverhalten (erstes fokussierbares Element).
            if (valueInputRef.current) {
              event.preventDefault();
              valueInputRef.current.focus();
            }
          }}
        >
          {dialogState && !activeCampaign && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Kampagne nicht mehr verfügbar</AlertDialogTitle>
                <AlertDialogDescription>
                  Diese Kampagne steht nicht mehr in der aktuellen Übersicht. Bitte schließen und den
                  Abschnitt neu öffnen.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Schließen</AlertDialogCancel>
              </AlertDialogFooter>
            </>
          )}

          {dialogState && activeCampaign && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {LEVER_LABEL[dialogState.lever]}: {activeCampaign.name ?? activeCampaign.resource_name}
                </AlertDialogTitle>
                {/*
                  M7 (Task-Review): NUR der Beschreibungssatz steht hier --
                  Formularfelder gehoeren nicht in eine `AlertDialogDescription`
                  (die rendert als <p>, ein <input> darin ist unzulaessiges
                  verschachteltes interaktives Markup und ein A11y-Fehler).
                */}
                <AlertDialogDescription asChild>
                  <span className="block space-y-1.5 text-sm text-muted-foreground">
                    {!preview ? (
                      <>
                        {dialogState.lever === 'budget' && (
                          <span className="block">
                            Aktuell {formatMoney(activeCampaign.budget.amount_micros, currency)}/Tag.
                          </span>
                        )}
                        {dialogState.lever === 'troas' && (
                          <>
                            <span className="block">
                              {activeCampaign.bidding.target_roas !== null
                                ? `Aktuell ${formatRatio(activeCampaign.bidding.target_roas)}.`
                                : `Noch kein Ziel gesetzt. Ist-ROAS 30 Tage: ${formatRatio(activeCampaign.measured_roas_30d)} (Anker der Erstsetzung).`}
                            </span>
                            <span className="block text-xs">
                              Gate der Erstsetzung: {int(activeCampaign.troas_first_set_gate.conversions_30d)}{' '}
                              Conversions in 30 Tagen, {int(activeCampaign.troas_first_set_gate.campaign_age_days)}{' '}
                              Tage alt.
                            </span>
                          </>
                        )}
                        {dialogState.lever === 'pause' && (
                          <span className="block">
                            Pausiert die Kampagne bei Google. Das Wieder-Einschalten bleibt eine
                            menschliche Handlung außerhalb dieses Steuerpults.
                          </span>
                        )}
                        {previewErrorCode && (
                          <span className="block font-medium text-destructive-ink">
                            {structureErrorText(previewErrorCode)}
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        <span className="block font-medium text-foreground">
                          {LEVER_LABEL[preview.lever]} {preview.name ?? preview.campaign}:{' '}
                          <PreviewSummaryLine preview={preview} currency={currency} />
                        </span>
                        {preview.warnings.includes('evaluation_window') && (
                          <span className="block text-warning-ink">
                            Bewertungsfenster läuft, diese Kampagne noch nicht an den Zahlen messen.
                          </span>
                        )}
                        {preview.next_allowed_at && (
                          <span className="block text-xs">
                            Nächste Änderung dieses Hebels frühestens {dateTime(preview.next_allowed_at)}.
                          </span>
                        )}
                      </>
                    )}
                  </span>
                </AlertDialogDescription>
              </AlertDialogHeader>

              {/* M7: Eingabefelder ausserhalb der Description. */}
              {!preview ? (
                dialogState.lever !== 'pause' && (
                  <div className="space-y-1.5">
                    <Label htmlFor="ads-structure-value">
                      {dialogState.lever === 'budget'
                        ? `Neues Tagesbudget (${currency ?? 'CHF'})`
                        : 'Neues Wertziel (Verhältnis, z.B. 5,50)'}
                    </Label>
                    <Input
                      id="ads-structure-value"
                      ref={valueInputRef}
                      inputMode="decimal"
                      value={valueInput}
                      onChange={(event) => setValueInput(event.target.value)}
                      placeholder={dialogState.lever === 'budget' ? 'z.B. 300,00' : 'z.B. 5,50'}
                    />
                  </div>
                )
              ) : (
                <div className="space-y-1.5">
                  <Label htmlFor="ads-structure-reason">
                    Begründung{reasonRequired ? ' (Pflicht)' : ' (optional)'}
                  </Label>
                  <Textarea
                    id="ads-structure-reason"
                    value={reasonInput}
                    onChange={(event) => setReasonInput(event.target.value)}
                    rows={2}
                    placeholder="Was ist der Anlass für diese Änderung?"
                  />
                </div>
              )}

              <AlertDialogFooter>
                {/*
                  Schliesst ueber `onOpenChange` am `AlertDialog`-Root, der
                  `closeDialog()` ruft. M9: waehrend der REALE Lauf unterwegs
                  ist (`confirmPending`), gesperrt -- ein Abbrechen mitten im
                  Google-Schreibzugriff wuerde die Sitzung wechseln, WAEHREND
                  `handleConfirm` noch auf die Antwort wartet, und genau davor
                  schuetzt die Sitzungspruefung dort bereits; die Sperre hier
                  macht den Zustand zusaetzlich fuer den Nutzer sichtbar.
                */}
                <AlertDialogCancel disabled={confirmPending}>Abbrechen</AlertDialogCancel>
                {!preview ? (
                  <Button onClick={() => void handlePreview()} disabled={previewPending}>
                    {previewPending ? 'lädt …' : 'Vorschau'}
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => setPreview(null)}
                      disabled={confirmPending}
                    >
                      Zurück
                    </Button>
                    <Button onClick={() => void handleConfirm()} disabled={confirmDisabled}>
                      {confirmPending ? 'wird geschrieben …' : 'Jetzt ändern'}
                    </Button>
                  </>
                )}
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

export default AdsStructureSection;
